import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { AreaChart } from "../../components/charts/area-chart";
import { useAnomalySummary, useRefreshMLData } from "../../hooks/use-ml";
import { useToast } from "../../hooks/use-toast";
import { clampPct } from "../../lib/utils";
import { getAIConfig } from "../ai-configuration";
import { 
  AlertTriangle, 
  RefreshCw, 
  Loader2, 
  AlertCircle, 
  Clock, 
  CheckCircle2, 
  LineChart,
  Monitor
} from "lucide-react";

export default function AnomalyDetection() {
  const { toast } = useToast();
  const cfg = getAIConfig();
  const [selectedSeverity, setSelectedSeverity] = useState(() => cfg.anomaly.defaultSeverityFilter);

  // Fetch anomaly data from backend (uses AI Configuration: limit, mode, sensitivity)
  const { 
    data: anomalyData, 
    isLoading, 
    error
  } = useAnomalySummary(cfg.anomaly.summaryLimit, cfg.general.mlMode, cfg.anomaly.sensitivity);
  
  const refreshMLData = useRefreshMLData();

  // Handle refresh
  const handleRefresh = async () => {
    try {
      await refreshMLData.mutateAsync();
      toast({
        title: "Data Refreshed",
        description: "Anomaly detection data has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Refresh Failed",
        description: "Failed to refresh anomaly data. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Use real data or fallback to empty array
  const anomalies = anomalyData?.data?.anomalies || [];
  const anomalyStats = anomalyData?.data || {
    total: 0,
    high: 0,
    medium: 0,
    low: 0,
    active: 0,
    investigating: 0,
    resolved: 0
  };

  // Filter anomalies by severity - removed duplicate declaration

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "High": return "bg-red-100 text-red-800";
      case "Medium": return "bg-yellow-100 text-yellow-800";
      case "Low": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active": return "bg-red-100 text-red-800";
      case "Investigating": return "bg-yellow-100 text-yellow-800";
      case "Resolved": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const filteredAnomalies = anomalies.filter((anomaly: any) => {
    if (selectedSeverity !== "all" && anomaly.severity !== selectedSeverity) {
      return false;
    }
    return true;
  });

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-dark flex items-center">
              <AlertTriangle className="h-6 w-6 text-orange-500 mr-3" />
              Anomaly Detection
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              AI-powered detection of unusual patterns and behaviors in your network
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshMLData.isPending || isLoading}
            >
              {refreshMLData.isPending || isLoading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              {refreshMLData.isPending || isLoading ? "Refreshing..." : "Refresh Analysis"}
            </Button>
            {/* <Button size="sm">
              <Settings className="h-4 w-4 mr-1" />
              Configure
            </Button> */}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading anomaly detection data...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Failed to load anomaly data</h3>
              <p className="text-sm text-red-600 mt-1">
                There was an error loading the anomaly detection data. Please try refreshing.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {!isLoading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Anomalies</p>
                <p className="text-2xl font-bold text-gray-900">{anomalyStats.total}</p>
              </div>
              <AlertTriangle className="h-6 w-6 text-gray-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">High Severity</p>
                <p className="text-2xl font-bold text-red-500">{anomalyStats.high}</p>
              </div>
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active</p>
                <p className="text-2xl font-bold text-orange-500">{anomalyStats.active}</p>
              </div>
              <Clock className="h-6 w-6 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Resolved</p>
                <p className="text-2xl font-bold text-green-500">{anomalyStats.resolved}</p>
              </div>
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Main Content */}
      {!isLoading && !error && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Anomaly List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Detected Anomalies</CardTitle>
                  <CardDescription>AI-detected unusual patterns and behaviors</CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedSeverity}
                    onChange={(e) => setSelectedSeverity(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Severities</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredAnomalies.length > 0 ? (
                <div className="space-y-4">
                  {filteredAnomalies.map((anomaly: any) => (
                  <div key={anomaly.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Monitor className="h-5 w-5 text-gray-500" />
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{anomaly.device_name || anomaly.device}</span>
                            <span className="text-xs text-gray-500">{anomaly.device_id || anomaly.device}</span>
                          </div>
                          <Badge className={getSeverityColor(anomaly.severity)}>
                            {anomaly.severity}
                          </Badge>
                          <Badge className={getStatusColor(anomaly.status)}>
                            {anomaly.status}
                          </Badge>
                        </div>
                        <h4 className="font-medium text-gray-900 mb-1">{anomaly.type}</h4>
                        <p className="text-sm text-gray-600 mb-2">{anomaly.description}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                          <span>{new Date(anomaly.timestamp).toLocaleString()}</span>
                          <span>Confidence: {clampPct(anomaly.confidence)}%</span>
                          <span>Impact: {anomaly.impact}</span>
                        </div>
                        
                        {/* Metrics */}
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div className="bg-gray-50 p-2 rounded">
                            <div className="text-gray-500">CPU</div>
                            <div className="font-medium">{clampPct(anomaly.metrics?.cpu)}%</div>
                          </div>
                          <div className="bg-gray-50 p-2 rounded">
                            <div className="text-gray-500">Memory</div>
                            <div className="font-medium">{clampPct(anomaly.metrics?.memory)}%</div>
                          </div>
                          <div className="bg-gray-50 p-2 rounded">
                            <div className="text-gray-500">Network</div>
                            <div className="font-medium">{clampPct(anomaly.metrics?.network)}%</div>
                          </div>
                        </div>
                      </div>
                      {/* <div className="flex flex-col space-y-2 ml-4">
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          Investigate
                        </Button>
                        <Button variant="outline" size="sm">
                          <Check className="h-4 w-4 mr-1" />
                          Resolve
                        </Button>
                      </div> */}
                    </div>
                  </div>
                ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <div className="text-center">
                    <AlertTriangle className="h-12 w-12 mb-2 text-gray-500" />
                    <p className="text-lg font-medium">No anomalies detected</p>
                    <p className="text-sm">Your network is running smoothly!</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Anomaly Trends */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Anomaly Trends</CardTitle>
              <CardDescription>Detection patterns over time</CardDescription>
            </CardHeader>
            <CardContent>
              {anomalies.length > 0 ? (
                <div className="h-64">
                  <AreaChart
                    title=""
                    data={anomalies.slice(0, 10).map((anomaly: any) => ({
                      time: new Date(anomaly.timestamp).toLocaleTimeString(),
                      value: anomaly.confidence
                    }))}
                    color="#f59e0b"
                    height={200}
                  />
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <LineChart className="h-12 w-12 mb-2 text-gray-500" />
                    <p>No anomaly data available</p>
                    <p className="text-sm">Anomalies will appear here when detected</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Detection Rules</CardTitle>
              <CardDescription>Active anomaly detection rules</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">CPU Usage Spike</span>
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Memory Leak Detection</span>
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Network Latency</span>
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Connection Anomalies</span>
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
}
